import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Card,
  CollapsableCard,
  Form,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import { NEGATIVE_TRANSFERS } from 'modules/attendance/lib/enums'
import { UPDATE_FREQUENCY } from 'modules/attendance/lib/enums'
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import attendanceSettings from 'modules/attendance/store/collections/settings'
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import { useTimeoffCompensations } from 'modules/attendance/store/collections/timeoffCompensation'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import type BalanceCarryover from 'modules/attendance/store/models/BalanceCarryover'
import type TimeoffCompensationForm from 'modules/timeoff/lib/TimeoffCompensationForm'

import CardContent from './CardContent'
import Content from './_Content'

interface Props {
  attendanceSetting: AttendanceSetting
  compensationForm: TimeoffCompensationForm
  balanceCarryover?: BalanceCarryover
  setBalanceCarryover?: (balanceCarryover: BalanceCarryover) => void
  hasExtraHourRules: boolean
  hasLimitBalancePool: boolean
}

const TimeoffCompensation = ({
  attendanceSetting,
  compensationForm,
  balanceCarryover,
  setBalanceCarryover,
  hasExtraHourRules,
  hasLimitBalancePool,
}: Props) => {
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const [running, setRunning] = React.useState(false)
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const settingId = attendanceSetting.attributes.id
  const compensationCollection = useTimeoffCompensations()
  const currentTimeoffCompensation = compensationCollection.last()

  const compensationEnableField = compensationForm.get('compensation_enabled')
  const compensationEnable = compensationEnableField.value

  const handleToggleCompensationEnabled = (newValue: boolean) => {
    compensationEnableField.set(newValue)

    if (!newValue && !currentTimeoffCompensation?.isDefault) {
      resetValues()
    }
  }

  const onClose = () => {
    setOpenEffectiveAtFormModal(false)
    compensationForm.resetAll()
  }

  const resetValues = async () => {
    await asyncAction(() =>
      compensationCollection.create({
        ...compensationForm.values,
        compensation_enabled: false,
        has_threshold_enabled: false,
        negative_transfers: NEGATIVE_TRANSFERS.unlimited,
        attendance_employees_setting_id: settingId,
      })
    )
  }

  const createSettings = async (newSettingId: string) => {
    setRunning(true)

    const hasUnlimitedCompensation = compensationForm.get('has_unlimited_compensation').value

    await asyncAction(() =>
      compensationCollection.create({
        ...compensationForm.values,
        compensation_enabled: true,
        attendance_employees_setting_id: newSettingId,
        has_unlimited_compensation: hasUnlimitedCompensation,
      })
    )

    if (balanceCarryover && setBalanceCarryover) {
      const attendanceSetting = await attendanceSettings.get(newSettingId)?.fetch()
      const balanceCarryoverId = attendanceSetting?.balance_carryover_id

      balanceCarryover.set({
        id: balanceCarryoverId,
        attendance_employees_setting_id: newSettingId,
      })

      await balanceCarryover.fetch()
      setBalanceCarryover(balanceCarryover)
    }

    setOpenEffectiveAtFormModal(false)
    setRunning(false)
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const newSettingId = newSetting.id

    createSettings(newSettingId)

    const routeParams = {
      id: 'time_tracking',
      settingId: newSettingId.toString(),
      tabId: 'compensation',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onSubmit = async () => {
    createSettings(settingId)
  }

  const title = i18n.t('time_tracking.settings.overtime.compensation.timeoff.form.card.title')
  const description = i18n.t(
    'time_tracking.settings.overtime.compensation.timeoff.form.card.description'
  )

  const formFrequency = compensationForm.get('frequency').value

  const shouldUpdateBalanceReset =
    compensationEnable && balanceCarryover
      ? (balanceCarryover.frequencyWeekly && formFrequency === UPDATE_FREQUENCY.monthly) ||
        (!balanceCarryover.frequencyWeekly && formFrequency === UPDATE_FREQUENCY.weekly)
      : false

  const onSubmitForm = () => {
    setOpenEffectiveAtFormModal(true)
  }

  return (
    <>
      <Form padding="s0" onSubmit={onSubmitForm}>
        {!hasExtraHourRules && (
          <CollapsableCard
            open={Boolean(compensationEnable)}
            Card={
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Box gap="s8">
                  <Text.Normal weight="medium">{title}</Text.Normal>
                  <Text.Normal>{description}</Text.Normal>
                </Box>
                <Box>
                  <TogglerCheckbox
                    value={compensationEnable}
                    onChange={(newValue) => handleToggleCompensationEnabled(newValue)}
                    labelBefore
                  />
                </Box>
              </Box>
            }
            Content={
              <Content
                form={compensationForm}
                shouldUpdateBalanceReset={shouldUpdateBalanceReset}
              />
            }
          />
        )}
      </Form>

      {hasExtraHourRules && (
        <CollapsableCard
          open
          Card={
            <Box gap="s8" padding="s8">
              <Card.Title size="small">
                {i18n.t('time_tracking.settings.compensation.time_off')}
              </Card.Title>
              <Card.Description>
                {i18n.t('time_tracking.settings.compensation.time_off_description')}
              </Card.Description>
            </Box>
          }
          Content={
            <CardContent
              form={compensationForm}
              onSubmit={onSubmitForm}
              hasLimitBalancePool={hasLimitBalancePool}
            />
          }
        />
      )}
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onSubmit}
          onClose={onClose}
          running={running}
          attendanceSettingId={attendanceSetting.attributes.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
    </>
  )
}

export default observer(TimeoffCompensation)
